import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useColorMode,
} from "@chakra-ui/react";
import { Key } from "lucide-react";

interface SignInModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSignIn: () => void;
  onSwitchToSignUp: () => void;
  signInKey: string;
  setSignInKey: (value: string) => void;
  signInError: string;
  setSignInError: (value: string) => void;
  isSigningIn: boolean;
}

export function SignInModal({
  isOpen,
  onCancel,
  onSignIn,
  onSwitchToSignUp,
  signInKey,
  setSignInKey,
  signInError,
  setSignInError,
  isSigningIn,
}: SignInModalProps) {
  const { colorMode } = useColorMode();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="md"
      isCentered
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent
        bg={colorMode === "dark" ? "stone.800" : "white"}
        borderRadius="xl"
        overflow="hidden"
      >
        <ModalHeader
          pb={4}
          pt={6}
          borderBottomWidth="1px"
          borderColor={colorMode === "dark" ? "stone.700" : "stone.100"}
        >
          <VStack spacing={1} align="start">
            <Text fontSize="xl" fontWeight="bold">
              Access Anonymous Account
            </Text>
            <Text fontSize="sm" fontWeight="normal" color="stone.500">
              Sign in with your secret key
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton top={6} />
        <ModalBody pt={6} pb={6}>
          <VStack spacing={5} align="stretch">
            <FormControl isInvalid={!!signInError}>
              <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                API Key
              </FormLabel>
              <InputGroup size="lg">
                <InputLeftAddon
                  fontFamily="mono"
                  fontSize="sm"
                  borderLeftRadius="lg"
                  borderRightRadius="0"
                >
                  mk_
                </InputLeftAddon>
                <Input
                  value={
                    signInKey.startsWith("mk_") ? signInKey.slice(3) : signInKey
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setSignInKey(val.startsWith("mk_") ? val : `mk_${val}`);
                    setSignInError("");
                  }}
                  placeholder="your-secret-key"
                  fontFamily="mono"
                  onKeyDown={(e) => e.key === "Enter" && onSignIn()}
                  autoFocus
                  borderLeftRadius="0"
                  borderRightRadius="lg"
                />
              </InputGroup>
              {signInError ? (
                <FormErrorMessage>{signInError}</FormErrorMessage>
              ) : (
                <Text fontSize="xs" color="stone.500" mt={2}>
                  Enter the secret key you received when creating your account
                </Text>
              )}
            </FormControl>

            <Box
              p={4}
              bg={colorMode === "dark" ? "blue.900" : "blue.50"}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={colorMode === "dark" ? "blue.700" : "blue.200"}
            >
              <VStack spacing={3} align="stretch">
                <HStack spacing={2}>
                  <Icon as={Key} boxSize={4} color="blue.400" />
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={colorMode === "dark" ? "blue.200" : "blue.800"}
                  >
                    Don't have an account?
                  </Text>
                </HStack>
                <Text
                  fontSize="xs"
                  color={colorMode === "dark" ? "blue.300" : "blue.700"}
                >
                  Create a new anonymous account to get started with secure
                  markdown sharing
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  onClick={onSwitchToSignUp}
                  _hover={{
                    bg: colorMode === "dark" ? "blue.800" : "blue.100",
                  }}
                >
                  Create Anonymous Account
                </Button>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter
          gap={3}
          pt={4}
          pb={6}
          borderTopWidth="1px"
          borderColor={colorMode === "dark" ? "stone.700" : "stone.100"}
        >
          <Button
            variant="ghost"
            onClick={onCancel}
            size="lg"
            flex={1}
          >
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={onSignIn}
            isLoading={isSigningIn}
            isDisabled={!signInKey.trim()}
            size="lg"
            flex={1}
          >
            Access Account
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
